import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createAccountServiceAccount,
  deleteAccountServiceAccount,
  listAccountServiceAccounts,
  rotateAccountServiceAccountKeys,
  updateAccountServiceAccount,
} from "../../api/endpoints/accountServiceAccounts.js";
import { filterBySearch } from "../services/entityFilters.js";

export function useAccountServiceAccounts(accountId) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [keysReveal, setKeysReveal] = useState(null);
  const [rotateTarget, setRotateTarget] = useState(null);

  const reload = useCallback(async () => {
    if (!accountId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await listAccountServiceAccounts(accountId);
      setItems(data);
    } catch (err) {
      setError(err.message ?? "Failed to load service accounts");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const visible = useMemo(
    () => filterBySearch(items, search, ["name", "publicKey"]),
    [items, search],
  );

  const openCreate = useCallback(() => {
    setEditing(null);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((row) => {
    setEditing(row);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditing(null);
  }, []);

  const closeKeysReveal = useCallback(() => setKeysReveal(null), []);
  const closeRotateConfirm = useCallback(() => setRotateTarget(null), []);

  const saveServiceAccount = useCallback(async (payload) => {
    if (!accountId) return;
    setSaving(true);
    setError(null);
    try {
      if (editing) {
        const updated = await updateAccountServiceAccount(accountId, editing.id, payload);
        setItems((prev) => prev.map((row) => (row.id === editing.id ? updated : row)));
        closeModal();
      } else {
        const created = await createAccountServiceAccount(accountId, payload);
        setItems((prev) => [...prev, created]);
        closeModal();
        setKeysReveal({
          title: "Service account created",
          subtitle: "Copy both keys now. The private key will be masked after you close this dialog.",
          publicKey: created.publicKey,
          privateKey: created.privateKey,
        });
      }
    } catch (err) {
      setError(err.message ?? "Failed to save service account");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [accountId, editing, closeModal]);

  const confirmRotateKeys = useCallback(async () => {
    if (!accountId || !rotateTarget) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await rotateAccountServiceAccountKeys(accountId, rotateTarget.id);
      setItems((prev) => prev.map((row) => (row.id === rotateTarget.id ? updated : row)));
      setRotateTarget(null);
      setKeysReveal({
        title: "Keys rotated",
        subtitle: "The previous private key is now invalid. Copy the new keys before closing.",
        publicKey: updated.publicKey,
        privateKey: updated.privateKey,
      });
    } catch (err) {
      setError(err.message ?? "Failed to rotate keys");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [accountId, rotateTarget]);

  const removeServiceAccount = useCallback(async (serviceAccountId) => {
    if (!accountId) return;
    setSaving(true);
    setError(null);
    try {
      await deleteAccountServiceAccount(accountId, serviceAccountId);
      setItems((prev) => prev.filter((row) => row.id !== serviceAccountId));
    } catch (err) {
      setError(err.message ?? "Failed to delete service account");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [accountId]);

  return {
    items: visible,
    allItems: items,
    loading,
    saving,
    error,
    search,
    setSearch,
    modalOpen,
    editing,
    keysReveal,
    rotateTarget,
    openCreate,
    openEdit,
    closeModal,
    closeKeysReveal,
    openRotateConfirm: setRotateTarget,
    closeRotateConfirm,
    confirmRotateKeys,
    saveServiceAccount,
    removeServiceAccount,
    reload,
    total: items.length,
  };
}
